import React from 'react';
import Images1 from '../Images/Images1.jpg';
import Images2 from '../Images/Images2.jpg';
import Images3 from '../Images/Images3.jpg';
import Images4 from '../Images/Images4.jpg';

/**
 * Modern Food Showcase Component
 * Replaces carousel with sophisticated grid layout featuring glass morphism
 * and enhanced visual hierarchy
 */
const FoodShowcase = ({ onDiscoverMore }) => {
  const cuisineFeatures = [
    {
      src: Images1,
      alt: "Mediterranean seafood paella with saffron",
      cuisine: "Mediterranean",
      description: "Rich paella with fresh seafood",
      highlight: "Authentic"
    },
    {
      src: Images2,
      alt: "Traditional Asian stir-fry with vegetables",
      cuisine: "Asian",
      description: "Vibrant stir-fry with garden vegetables",
      highlight: "Fresh"
    },
    {
      src: Images3,
      alt: "Rustic Italian pasta with fresh herbs",
      cuisine: "Italian",
      description: "Handmade pasta with aromatic herbs",
      highlight: "Traditional"
    },
    {
      src: Images4,
      alt: "Colorful Middle Eastern mezze platter",
      cuisine: "Middle Eastern",
      description: "Diverse mezze with exotic flavors",
      highlight: "Exotic"
    }
  ];

  return (
    <div className="mb-8 w-full sm:mb-12 lg:mb-16">
      {/* Section Header */}
      <div className="mb-6 text-center sm:mb-10">
        <h3 className="mb-2 animate-fade-in text-xl font-bold leading-tight text-white sm:mb-4 sm:text-3xl lg:text-title">
          Explore Global Flavors
        </h3>
        <p className="mx-auto max-w-2xl animate-fade-in px-2 text-xs leading-relaxed text-white/80 animation-delay-100 sm:text-base lg:text-lg">
          From comfort foods to exotic delicacies, discover the world's most beloved dishes
        </p>
      </div>

      {/* Modern Grid Layout */}
      <div className="grid grid-cols-1 gap-4 px-3 md:grid-cols-2 md:gap-6 lg:grid-cols-4 lg:gap-8 sm:px-4">
        {cuisineFeatures.map((item, index) => (
          <div
            key={index}
            className="food-showcase-float"
            style={{
              '--float-delay': `${index * 0.65}s`,
              '--float-lg-base-y': index % 2 === 0 ? '1.5rem' : '-1.5rem'
            }}
          >
            <div
              className="group relative animate-scale-in overflow-hidden rounded-xl border border-white/35 bg-glass-light shadow-[0_22px_55px_rgba(87,28,9,0.32),0_8px_18px_rgba(255,255,255,0.14)] ring-1 ring-white/20 backdrop-blur-md transition-all duration-500 hover:-translate-y-1 hover:border-white/60 hover:shadow-[0_32px_75px_rgba(87,28,9,0.42),0_0_38px_rgba(255,237,213,0.28)] hover:ring-orange-100/45 sm:rounded-2xl"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Image Container */}
              <div className="aspect-[5/6] overflow-hidden sm:aspect-[4/5]">
                <img
                  src={item.src}
                  alt={item.alt}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-90" />

                {/* Content Overlay */}
                <div className="absolute bottom-0 left-0 right-0 translate-y-1 transform p-4 transition-transform duration-300 group-hover:translate-y-0 sm:translate-y-2 sm:p-6">
                  {/* Highlight Badge */}
                  <div className="mb-2 inline-block rounded-full bg-orange-500 px-2.5 py-0.5 text-xs font-semibold text-white shadow-lg sm:mb-3 sm:px-3 sm:py-1 sm:text-sm">
                    {item.highlight}
                  </div>

                  {/* Cuisine Name */}
                  <h4 className="mb-1 text-base font-bold tracking-tight text-white sm:mb-2 sm:text-xl">
                    {item.cuisine}
                  </h4>

                  {/* Description */}
                  <p className="text-xs leading-relaxed text-white/90 opacity-0 transition-opacity delay-100 duration-300 group-hover:opacity-100 sm:text-sm">
                    {item.description}
                  </p>
                </div>
              </div>

              {/* Hover Effect Border */}
              <div className="pointer-events-none absolute inset-0 rounded-xl border-2 border-orange-400/0 transition-colors duration-300 group-hover:border-orange-400/50 sm:rounded-2xl" />
            </div>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="mt-7 animate-fade-in text-center animation-delay-500 sm:mt-10">
        <p className="mb-2.5 text-sm text-white/70 sm:mb-4 sm:text-xl">
          Ready to explore these amazing cuisines?
        </p>
        <button
          type="button"
          onClick={onDiscoverMore}
          className="discover-more-flash group inline-flex cursor-pointer items-center gap-1.5 text-sm text-orange-200 transition-colors duration-300 hover:text-orange-100 sm:gap-2 sm:text-xl"
        >
          <span className="font-semibold">Discover More Recipes</span>
          <span className="discover-more-arrow">→</span>
        </button>
      </div>
    </div>
  );
};

export default FoodShowcase;
